import { useCallback, useState } from "react";

/**
 * 브라우저에 남는 참/거짓 상태 — 접기·펼치기 같은 **화면 배치 취향**용.
 *
 * 2026-09-04 (운영자): "접기가 있는 스티키 메뉴, 보조지표 등은 직전 상태를
 * 유지하도록 만들 수 있나요? 새로고침하면 다 원상복귀돼서 좀 불편하네."
 *
 * ## 어디에 저장하나
 *
 * `localStorage` 다. 배치 취향은 **그 사람의 것**이지 분석의 것도, 사건의
 * 것도 아니다. 서버에 두면 분석 데이터에 사용자 취향이 섞이고, store 에 두면
 * 새로고침에 사라진다(지금 문제).
 *
 * ## 무엇을 저장하지 않나
 *
 * **선택한 후보·RT·확대 구간은 저장하지 않는다.** 그건 배치가 아니라 판독
 * 상태이고, 화면을 다시 열었을 때 이전 사람의 선택이 남아 있으면 "지금 보고
 * 있는 것"을 오해하게 된다(증거 화면에서는 위험하다). 저장 대상은 패널이
 * 접혀 있는지 같은 **보기 방식**뿐이다.
 *
 * ## 저장이 막혀 있어도 동작한다
 *
 * 사생활 보호 모드나 브라우저 설정에서는 접근 자체가 예외를 던진다. 읽기·쓰기
 * 모두 감싸서, 저장이 안 되면 **이번 세션에서만 유지**되고 화면은 그대로
 * 동작한다. 저장은 편의지 기능이 아니다.
 */
export function usePersistentBoolean(
  key: string,
  fallback: boolean,
): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === "true") return true;
      if (stored === "false") return false;
    } catch {
      // 저장소를 못 읽는다 — 기본값으로 시작한다.
    }
    return fallback;
  });

  const update = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        try {
          localStorage.setItem(key, String(resolved));
        } catch {
          // 저장은 실패해도 이번 세션의 화면은 바뀐 대로 둔다.
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, update];
}
